import React, { useEffect, useMemo, useState } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {
  formatPresetLabel,
  getLatestScheduleTime,
  getSchedulePresets,
  getScheduleWindowError,
} from "./scheduledMessageUtils";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 460,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "85vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: 24,
  outline: "none",
  p: 3,
};

const FIELD_SX = {
  backgroundColor: "white",
  borderRadius: "10px",
  fontSize: "14px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9", borderWidth: "1px" },
};

const pickerSlotProps = {
  day: { sx: { "&.MuiPickersDay-root.Mui-selected": { backgroundColor: "#6d28d9" } } },
  digitalClockSectionItem: {
    sx: {
      "&.Mui-selected": { backgroundColor: "#6d28d9", color: "white" },
      "&.Mui-selected:hover, &.Mui-selected:focus": { backgroundColor: "#5b21b6" },
    },
  },
  actionBar: { sx: { "& .MuiButton-root": { color: "#6d28d9" } } },
};

const pickerSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: "40px",
    backgroundColor: "white",
    borderRadius: "10px",
    fontSize: "14px",
    "&.Mui-focused fieldset": { borderColor: "#6d28d9" },
  },
};

// The caret menu on the send button: Slack's presets plus "Custom time".
// Presets past what the message's files allow are left out.
export function ScheduleMessageMenu({ anchorEl, onClose, onPick, onCustom, attachments, channelDeadline }) {
  const open = Boolean(anchorEl);
  const presets = useMemo(() => {
    if (!open) return [];
    const now = new Date();
    return getSchedulePresets(now)
      .filter((preset) => !getScheduleWindowError(preset.sendAt, attachments, now, channelDeadline))
      .map((preset) => ({ ...preset, label: formatPresetLabel(preset, now) }));
  }, [open, attachments, channelDeadline]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "bottom", horizontal: "right" }}
    >
      <p className="px-4 pb-1 pt-1 text-xs font-semibold text-slate-500">Schedule message</p>
      {presets.map((preset) => (
        <MenuItem
          key={preset.key}
          sx={{ fontSize: 14 }}
          onClick={() => {
            onClose();
            onPick(preset.sendAt);
          }}
        >
          {preset.label}
        </MenuItem>
      ))}
      <Divider sx={{ my: 0.5 }} />
      <MenuItem
        sx={{ fontSize: 14 }}
        onClick={() => {
          onClose();
          onCustom();
        }}
      >
        Custom time
      </MenuItem>
    </Menu>
  );
}

// Pick a date and time (and, when editing, change the text). Used for
// "Custom time" when scheduling and for editing a scheduled message.
export function ScheduleTimeModal({
  open,
  onClose,
  onConfirm,
  title = "Schedule message",
  confirmLabel = "Schedule message",
  initialSendAt,
  initialText,
  editText = false,
  attachments,
  channelDeadline,
}) {
  const [sendAt, setSendAt] = useState(null);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fallback = getSchedulePresets()[0].sendAt;
    const initial =
      initialSendAt && new Date(initialSendAt) > new Date() ? initialSendAt : fallback;
    setSendAt(dayjs(initial));
    setText(initialText || "");
    setError("");
    setSaving(false);
  }, [open, initialSendAt, initialText]);

  const maxDateTime = useMemo(
    () => (open ? dayjs(getLatestScheduleTime(attachments, new Date(), channelDeadline)) : undefined),
    [open, attachments, channelDeadline]
  );

  const submit = async () => {
    const when = sendAt?.toDate?.();
    const windowError = getScheduleWindowError(when, attachments, new Date(), channelDeadline);
    if (windowError) {
      setError(windowError);
      return;
    }
    if (editText && !text.trim() && !(attachments || []).length) {
      setError("Message is empty.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onConfirm({ sendAt: when, text: editText ? text : undefined });
      onClose();
    } catch (confirmError) {
      setError(confirmError?.message || "Something went wrong. Please try again.");
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <div className="flex items-start justify-between gap-x-4">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        {editText && (
          <div className="mt-4">
            <label className="mb-1 block text-xs font-medium text-slate-500">Message</label>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={10}
              value={text}
              onChange={(event) => setText(event.target.value)}
              InputProps={{ sx: FIELD_SX }}
            />
          </div>
        )}
        <div className="mt-4">
          <label className="mb-1 block text-xs font-medium text-slate-500">Send at</label>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              value={sendAt}
              onChange={(value) => setSendAt(value)}
              disablePast
              maxDateTime={maxDateTime}
              slotProps={pickerSlotProps}
              sx={pickerSx}
            />
          </LocalizationProvider>
        </div>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            className="rounded-lg bg-violet-700 px-4 py-2 text-sm font-medium text-white hover:bg-violet-800 disabled:opacity-50"
            onClick={submit}
          >
            {saving ? "Saving..." : confirmLabel}
          </button>
        </div>
      </Box>
    </Modal>
  );
}
